import React, { useRef, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  TableHead,
  Table,
  Button,
  IconButton,
  Checkbox,
  TextField,
} from "@mui/material";
import ReactToPrint from "react-to-print";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Print } from "@mui/icons-material";
import { headingStyle, tableRowLab } from "../../style/LapPageStyle";
import { textfieldText } from "../../style/AppointmentStyle";
import { primaryBtnTeal } from "../../style/PatientDetailsStyle";

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatPrintTime = () => {
  const date = new Date();
  const options = {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  };
  return date.toLocaleString("en-US", options);
};

// Print-only component — renders data directly without any API call
const MergedPrintContent = React.forwardRef(({ reportData, patientData }, ref) => (
  <Box
    ref={ref}
    sx={{
      width: "97%",
      display: "none",
      "@media print": { display: "block" },
    }}
  >
    <Typography variant="heading_Large">
      {`${patientData?.name || patientData?.full_name || ""} (${patientData?.uid || ""})`}
    </Typography>
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        padding: "15px 2px 5px 2px",
      }}
    >
      <Typography sx={{ fontWeight: "bold" }} fontSize={"14px"}>
        {patientData?.age === 0 ? 1 : patientData?.age} / {patientData?.gender}
      </Typography>
      <Typography sx={{ fontWeight: "bold" }} fontSize={"14px"}>
        {patientData?.name || patientData?.full_name}
      </Typography>
      <Typography sx={{ fontWeight: "bold" }} fontSize={"14px"}>
        Mob: {patientData?.mobile}
      </Typography>
    </Box>
    <Table
      sx={{
        border: "1px solid lightgrey",
        borderBottom: "1.9px solid grey",
        width: "100%",
        mb: "1rem !important",
      }}
    >
      <TableHead>
        <TableRow>
          <TableCell
            align="center"
            sx={{ fontWeight: "bold", width: "34%", padding: "6px" }}
          >
            Test Name
          </TableCell>
          <TableCell
            align="center"
            sx={{ fontWeight: "bold", width: "20%", padding: "6px" }}
          >
            Lab
          </TableCell>
          <TableCell
            align="center"
            sx={{ fontWeight: "bold", width: "20%", padding: "6px" }}
          >
            Generated Date
          </TableCell>
          <TableCell
            align="center"
            sx={{ fontWeight: "bold", width: "26%", padding: "6px" }}
          >
            Result
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {reportData?.map((item, index) => (
          <TableRow key={item.id ?? index}>
            <TableCell align="center" sx={{ width: "34%", padding: "4px" }}>
              {item.name}
            </TableCell>
            <TableCell align="center" sx={{ width: "20%", padding: "4px" }}>
              {item.lab || "—"}
            </TableCell>
            <TableCell align="center" sx={{ width: "20%", padding: "4px" }}>
              {formatDate(item.generated_at)}
            </TableCell>
            <TableCell align="center" sx={{ width: "26%", padding: "4px" }}>
              {item.value === "Processing" || item.value === "Done"
                ? ""
                : item.value || "—"}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    <Typography variant="subtitle1" fontSize={"14px"} fontWeight={"600"} mt={-1}>
      Printed at {formatPrintTime()}
    </Typography>
  </Box>
));

const MergedReportPrint = ({ patientData, reportData, onClose }) => {
  const today = new Date().toISOString().split("T")[0];
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [checkedItems, setCheckedItems] = useState(reportData.map(() => true));
  const printRef = useRef();

  const dateFilteredData = reportData.filter((item) => {
    if (!item.generated_at) return true;
    const d = item.generated_at.split("T")[0];
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    return true;
  });

  const filteredReportData = dateFilteredData.filter((item) => {
    const originalIndex = reportData.indexOf(item);
    return checkedItems[originalIndex];
  });

  const handleCheckboxChange = (originalIndex) => {
    setCheckedItems((prev) =>
      prev.map((checked, i) => (i === originalIndex ? !checked : checked)),
    );
  };

  return (
    <Box sx={{ width: "80vw" }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={onClose} size="small">
            <ArrowBackIcon sx={{ color: "neutral.teal" }} />
          </IconButton>
          <Typography variant="heading_Large" fontWeight={700} sx={headingStyle}>
            Print Report
          </Typography>
        </Box>
      </Box>

      {/* Patient info */}
      <Grid
        container
        sx={{ justifyContent: "space-between", width: "100%", mt: "20px" }}
      >
        <Typography>Name: {patientData?.name || patientData?.full_name}</Typography>
        <Typography>Patient ID: {patientData?.uid}</Typography>
        <Typography>Mobile: {patientData?.mobile}</Typography>
        <Typography>Age: {patientData?.age}</Typography>
        <Typography>{patientData?.gender}</Typography>
      </Grid>

      {/* Date range filter */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#4A5568" }}>
            From:
          </Typography>
          <TextField
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            size="small"
            inputProps={{ max: dateTo || undefined }}
            sx={{ ...textfieldText, width: 155 }}
          />
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#4A5568" }}>
            To:
          </Typography>
          <TextField
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            size="small"
            inputProps={{ min: dateFrom || undefined }}
            sx={{ ...textfieldText, width: 155 }}
          />
        </Box>
      </Box>

      {/* Preview table */}
      <Grid container sm={12} md={12} marginBottom={"auto"} lg={12}>
        <Grid item xs={12}>
          <TableContainer
            sx={{
              minHeight: "30vh",
              mt: 2,
              width: "100%",
              overflowY: "scroll",
              "&::-webkit-scrollbar": { display: "none" },
              "-ms-overflow-style": "none",
              "scrollbar-width": "none",
            }}
          >
            <Table sx={{ width: "100%" }}>
              <TableHead stickyHeader>
                <TableRow>
                  <TableCell sx={{ fontSize: "14px", p: "6px", paddingLeft: "15px" }}>
                    Name
                  </TableCell>
                  <TableCell sx={{ fontSize: "14px", p: "6px" }}>Lab</TableCell>
                  <TableCell sx={{ fontSize: "14px", p: "6px" }}>
                    Generated Date
                  </TableCell>
                  <TableCell sx={{ fontSize: "14px", p: "6px" }}>Value</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dateFilteredData.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      sx={{
                        textAlign: "center",
                        py: 4,
                        color: "#94A3B8",
                        fontSize: "14px",
                      }}
                    >
                      No reports found for the selected date range.
                    </TableCell>
                  </TableRow>
                ) : (
                  dateFilteredData.map((item) => {
                    const originalIndex = reportData.indexOf(item);
                    return (
                      <TableRow
                        key={item.id ?? originalIndex}
                        sx={{
                          ...tableRowLab,
                          borderTop: "2px solid white",
                          borderBottom: "8px solid white",
                        }}
                      >
                        <TableCell
                          sx={{
                            padding: "4px",
                            paddingLeft: "15px",
                            borderTopLeftRadius: "20px",
                            borderBottomLeftRadius: "20px",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "15px",
                              fontWeight: "bold",
                              padding: "0px",
                            }}
                          >
                            <Checkbox
                              sx={{
                                height: "5px",
                                width: "5px",
                                marginRight: "4px",
                              }}
                              checked={checkedItems[originalIndex]}
                              onChange={() => handleCheckboxChange(originalIndex)}
                            />
                            {item.name}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ padding: "0px", paddingLeft: "15px" }}>
                          {item.lab || "—"}
                        </TableCell>
                        <TableCell sx={{ padding: "0px", paddingLeft: "15px" }}>
                          {formatDate(item.generated_at)}
                        </TableCell>
                        <TableCell
                          sx={{
                            padding: "8px",
                            borderTopRightRadius: "20px",
                            borderBottomRightRadius: "20px",
                          }}
                        >
                          <Typography variant="body_Grey">
                            {item.value === "Processing" || item.value === "Done"
                              ? ""
                              : item.value || "—"}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>

      {/* Print button */}
      <Grid
        container
        display={"flex"}
        justifyContent={"space-between"}
        sx={{ mt: 2 }}
        lg={12}
      >
        <Grid item />
        <Grid item>
          <ReactToPrint
            trigger={() => (
              <Button
                disabled={filteredReportData.length < 1}
                variant="contained"
                sx={primaryBtnTeal}
              >
                <IconButton>
                  <Print sx={{ height: "16px", width: "16px", color: "white" }} />
                </IconButton>
                <Typography fontSize={"1.1rem"}>Print</Typography>
              </Button>
            )}
            content={() => printRef.current}
            pageStyle="@page { size: auto; margin: 6mm; }"
            copyStyles={true}
          />
        </Grid>
      </Grid>

      {/* Hidden print content — uses filteredReportData directly, no API call */}
      <MergedPrintContent
        ref={printRef}
        reportData={filteredReportData}
        patientData={patientData}
      />
    </Box>
  );
};

export default MergedReportPrint;
